import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { Badge, Button } from 'react-bootstrap';
import ChatWorkspace from './ChatWorkspace';
import { useChatUi } from '../../context/ChatUiContext';
import { useAppSelector } from '../../store/hooks';

const MARGIN = 16;
const MIN_W = 640;
const MIN_H = 480;

function storageKey(userId?: string | null) {
  return `product_chat_panel_state_${userId || 'anon'}`;
}

function loadState(userId?: string | null) {
  try {
    return JSON.parse(localStorage.getItem(storageKey(userId)) || '{}') || {};
  } catch {
    return {};
  }
}

function saveState(userId: string | null | undefined, patch: Record<string, unknown>) {
  const prev = loadState(userId);
  localStorage.setItem(storageKey(userId), JSON.stringify({ ...prev, ...patch }));
}

/** Keep the whole panel inside the viewport (size + position). */
function clampBox(
  box: { x: number; y: number; width: number; height: number },
  minimized = false
) {
  const winW = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const winH = typeof window !== 'undefined' ? window.innerHeight : 800;
  const maxW = Math.max(220, winW - MARGIN * 2);
  const maxH = Math.max(44, winH - MARGIN * 2);
  const minW = minimized ? 220 : Math.min(MIN_W, maxW);
  const minH = minimized ? 44 : Math.min(MIN_H, maxH);
  const width = Math.min(Math.max(box.width, minW), maxW);
  const height = Math.min(Math.max(box.height, minH), maxH);
  const maxX = winW - width - MARGIN;
  const maxY = winH - height - MARGIN;
  return {
    width,
    height,
    x: Math.max(MARGIN, Math.min(maxX, box.x)),
    y: Math.max(MARGIN, Math.min(maxY, box.y)),
  };
}

/** Size from storage; always center in the current viewport on open. */
function initialBox(userId?: string | null) {
  const saved = loadState(userId);
  const winW = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const winH = typeof window !== 'undefined' ? window.innerHeight : 800;
  const w = Number(saved.width) || Math.min(960, winW - MARGIN * 2);
  const h = Number(saved.height) || Math.min(560, winH - MARGIN * 2);
  return clampBox({
    x: (winW - w) / 2,
    y: (winH - h) / 2,
    width: w,
    height: h,
  });
}

const ChatModal: React.FC = () => {
  const { modalOpen, minimized, productId, orderId, tab, setTab, setOrderId, closeModal, toggleMinimize } = useChatUi();
  const user = useAppSelector((s) => s.auth.user);
  const userId = user?.id;
  const [box, setBox] = useState(() => initialBox(userId));
  const [restoredHeight, setRestoredHeight] = useState(() => initialBox(userId).height);

  useEffect(() => {
    if (modalOpen) {
      // Re-center in the current viewport whenever the panel opens
      const next = initialBox(userId);
      setBox(next);
      setRestoredHeight(next.height);
    }
  }, [modalOpen, userId, productId]);

  useEffect(() => {
    if (!modalOpen) return;
    if (minimized) {
      setBox((prev) => {
        setRestoredHeight(prev.height > 80 ? prev.height : restoredHeight);
        return clampBox({ ...prev, width: Math.min(320, prev.width), height: 44 }, true);
      });
    } else {
      setBox((prev) => {
        if (prev.height >= MIN_H && prev.width >= MIN_W) return clampBox(prev);
        return clampBox({
          ...prev,
          width: Math.max(MIN_W, prev.width || 960),
          height: Math.max(MIN_H, restoredHeight || 560),
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimized]);

  useEffect(() => {
    if (!modalOpen) return;
    const onResize = () => setBox((prev) => clampBox(prev, minimized));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [modalOpen, minimized]);

  const handleDragStop = useCallback((_e: any, d: { x: number; y: number }) => {
    setBox((prev) => {
      const next = clampBox({ ...prev, x: d.x, y: d.y }, minimized);
      saveState(userId, { width: next.width, height: next.height });
      return next;
    });
  }, [minimized, userId]);

  const handleResize = useCallback((_e: any, _dir: any, ref: HTMLElement, _delta: any, pos: { x: number; y: number }) => {
    setBox(
      clampBox({
        x: pos.x,
        y: pos.y,
        width: ref.offsetWidth,
        height: ref.offsetHeight,
      })
    );
  }, []);

  const handleResizeStop = useCallback((_e: any, _dir: any, ref: HTMLElement, _delta: any, pos: { x: number; y: number }) => {
    const next = clampBox({
      x: pos.x,
      y: pos.y,
      width: ref.offsetWidth,
      height: ref.offsetHeight,
    });
    setBox(next);
    if (!minimized) {
      setRestoredHeight(next.height);
      saveState(userId, { width: next.width, height: next.height });
    }
  }, [minimized, userId]);

  if (!modalOpen) return null;

  const content = (
    <Rnd
      size={{ width: box.width, height: box.height }}
      position={{ x: box.x, y: box.y }}
      bounds="parent"
      minWidth={minimized ? 220 : Math.min(MIN_W, typeof window !== 'undefined' ? window.innerWidth - 32 : MIN_W)}
      minHeight={minimized ? 44 : Math.min(MIN_H, typeof window !== 'undefined' ? window.innerHeight - 32 : MIN_H)}
      maxWidth={typeof window !== 'undefined' ? window.innerWidth - 32 : 1600}
      maxHeight={typeof window !== 'undefined' ? window.innerHeight - 32 : 1000}
      disableDragging={false}
      enableResizing={
        minimized
          ? false
          : {
              top: true,
              right: true,
              bottom: true,
              left: true,
              topRight: true,
              bottomRight: true,
              bottomLeft: true,
              topLeft: true,
            }
      }
      onDragStop={handleDragStop}
      onResize={handleResize}
      onResizeStop={handleResizeStop}
      dragHandleClassName="chat-panel-drag-handle"
      style={{
        position: 'absolute',
        zIndex: 1100,
        pointerEvents: 'auto',
        display: 'flex',
        flexDirection: 'column',
        background: '#1a1d21',
        borderRadius: 8,
        boxShadow: '0 8px 32px rgba(0,0,0,0.55)',
        overflow: 'hidden',
        border: '1px solid #3d444d',
      }}
    >
      <div
        className="chat-panel-drag-handle"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: '#2b3035',
          borderBottom: minimized ? 'none' : '1px solid #3d444d',
          cursor: 'move',
          height: 44,
          flexShrink: 0,
        }}
      >
        <span style={{ fontWeight: 600, color: '#f1f3f5' }}>
          Chat {productId ? <Badge bg="secondary" className="ms-1">#{productId}</Badge> : null}
        </span>
        <div className="d-flex gap-1">
          <Button
            variant="link"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              toggleMinimize();
            }}
            style={{ color: '#adb5bd', padding: '0 4px', textDecoration: 'none' }}
          >
            {minimized ? '▢' : '—'}
          </Button>
          <Button
            variant="link"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
            style={{ color: '#adb5bd', padding: '0 4px' }}
          >
            ✕
          </Button>
        </div>
      </div>
      {!minimized && (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            background: '#1a1d21',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ChatWorkspace
            tab={tab}
            initialProductId={productId}
            initialOrderId={orderId}
            compact
            onTabChange={setTab}
            onSelectOrder={setOrderId}
          />
        </div>
      )}
    </Rnd>
  );

  const container =
    typeof document !== 'undefined' ? document.getElementById('chat-modal-root') ?? document.body : null;
  if (!container) return null;
  return createPortal(content, container);
};

export default ChatModal;
